import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sun,
  Moon,
  Layers,
  Infinity,
  Car,
  Plane,
  Phone,
  MessageCircle,
  Check,
  ArrowRight,
  Sparkles,
  Heart,
  ShieldCheck,
} from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

// ---------- DATA ----------
const PERCORSI = [
  {
    id: "diurno",
    icon: Sun,
    title: "Percorso Diurno",
    tagline: "La quotidianità con una presenza educativa stabile.",
    desc: "Accompagno la famiglia nella gestione della quotidianità durante le ore diurne, offrendo una presenza educativa stabile e competente che sostiene sia il bambino sia i genitori.",
    include: [
      "Cura quotidiana del neonato",
      "Gestione dei momenti di sonno, veglia e alimentazione",
      "Igiene e cambio",
      "Osservazione dello sviluppo",
      "Supporto alla routine",
      "Restituzione ai genitori",
    ],
    method:
      "Approccio Metodo T.A.T.A.® basato sull'ascolto dei segnali del bambino, sul rispetto dei tempi e sulla costruzione di una relazione sicura.",
  },
  {
    id: "notturno",
    icon: Moon,
    title: "Percorso Notturno",
    tagline: "Le notti diventano un tempo di fiducia.",
    desc: "Pensato per sostenere il neonato nei momenti più delicati della notte e per permettere ai genitori di recuperare energie in un clima di fiducia.",
    include: [
      "Gestione dei risvegli notturni",
      "Alimentazione notturna",
      "Contenimento e rassicurazione",
      "Supporto al ri-addormentamento",
      "Monitoraggio dei ritmi sonno-veglia",
    ],
    method:
      "Approccio Metodo T.A.T.A.® notturno, con attenzione alla regolazione emotiva, alla sicurezza e alla gradualità.",
  },
  {
    id: "integrato",
    icon: Layers,
    title: "Percorso Integrato",
    tagline: "Giorno e notte, con coerenza.",
    desc: "Assistenza coordinata tra giorno e notte per una presa in carico globale del neonato, favorendo stabilità, prevedibilità e serenità familiare.",
    include: [
      "Assistenza diurna e notturna coordinata",
      "Costruzione di routine coerenti",
      "Monitoraggio continuo",
      "Confronto costante con i genitori",
    ],
    method:
      "Intervento educativo integrato secondo il Metodo T.A.T.A.® con continuità e coerenza.",
  },
  {
    id: "continuativo",
    icon: Infinity,
    title: "Percorso Continuativo",
    tagline: "Un accompagnamento strutturato nel tempo.",
    desc: "Per le famiglie che desiderano un accompagnamento strutturato e duraturo, in grado di sostenere ogni fase dei primi mesi di vita.",
    include: [
      "Presenza programmata nel tempo",
      "Accompagnamento evolutivo",
      "Adattamento del percorso alle fasi di crescita",
      "Supporto continuativo",
    ],
    method:
      "Metodo T.A.T.A.® evolutivo, basato sulla relazione stabile e sulla personalizzazione progressiva.",
  },
];

const TRASFERTE = [
  {
    icon: Car,
    title: "Trasferta Oraria",
    desc: "Presenza, assistenza, cura e tempo di viaggio conteggiati come parte del percorso.",
  },
  {
    icon: Sun,
    title: "Trasferta Giornaliera",
    desc: "Presenza fino a 10 ore consecutive con reperibilità dedicata.",
  },
  {
    icon: Sparkles,
    title: "Weekend",
    desc: "Assistenza continuativa nel fine settimana, ideale per momenti di riposo o eventi familiari.",
  },
  {
    icon: Moon,
    title: "Notte in Trasferta",
    desc: "Notti fuori dal domicilio abituale, con la stessa cura del percorso notturno.",
  },
  {
    icon: Plane,
    title: "Ore Extra",
    desc: "Presenza aggiuntiva richiesta oltre le ore concordate, sempre nel rispetto della famiglia.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Primo contatto",
    desc: "Ci sentiamo per capire i bisogni del bambino e della vostra famiglia. Un momento per conoscerci e per farmi sentire già presente.",
  },
  {
    n: "02",
    title: "Colloquio informativo",
    desc: "Approfondiamo insieme la vostra situazione, la vostra storia e le vostre aspettative.",
  },
  {
    n: "03",
    title: "Definiamo le modalità",
    desc: "Concordiamo insieme le modalità operative del percorso — orari, presenza, routine, restituzione.",
  },
  {
    n: "04",
    title: "Pianifichiamo il calendario",
    desc: "Costruiamo insieme il calendario del percorso, adattandolo ai vostri tempi reali.",
  },
  {
    n: "05",
    title: "Accordo professionale",
    desc: "Sottoscriviamo un accordo professionale che definisce modalità organizzative ed economiche — a tutela di entrambi, con chiarezza e trasparenza.",
  },
];

const PRINCIPI = [
  "Rispetto dei tempi del bambino",
  "Centralità della relazione",
  "Osservazione professionale",
  "Comunicazione costante",
  "Assenza di giudizio",
  "Valorizzazione delle competenze genitoriali",
];

const waLink = (topic = "Assistenza Neonatale") => {
  const msg = encodeURIComponent(`Ciao Teresa, vorrei info sul ${topic} 🍼`);
  return `${BRAND.whatsappUrl}?text=${msg}`;
};

// ============================================================
// PAGE
// ============================================================
export default function AssistenzaNeonatale() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "80%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 25]);

  return (
    <div className="pt-24 md:pt-28" data-testid="assistenza-page">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[75vh] px-6 md:px-10 py-16 md:py-28 overflow-hidden bg-[#FAF8F5]"
      >
        <div className="max-w-[1500px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">
            — Assistenza Neonatale Specialistica
          </p>
          <h1
            className="font-display text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.85] text-[#3D2140] mb-8"
            data-testid="assistenza-heading"
          >
            <RevealText text={["I primi"]} delay={0.1} />
            <RevealText text={["mesi", "insieme."]} className="text-[#FF2768] block" delay={0.2} />
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mt-10 md:mt-16">
            <div className="md:col-span-7 space-y-5 text-[#3D2140]/80 text-base md:text-lg leading-relaxed">
              <p className="text-2xl md:text-3xl font-editorial italic text-[#3D2140]">
                Un percorso dedicato alle famiglie nei primi mesi di vita del bambino, un periodo delicato in cui cura, ascolto e competenza fanno la differenza.
              </p>
              <p>
                Attraverso una presenza attenta, rispettosa e competente, accompagno il neonato e i genitori nella costruzione delle prime routine quotidiane, favorendo il benessere emotivo, la sicurezza e un sano sviluppo del ritmo sonno-veglia.
              </p>
              <p>
                Il percorso integra competenze educative, osservazione professionale e supporto alla genitorialità. Ogni percorso viene progettato <em className="text-[#FF2768] not-italic font-semibold">su misura</em>, in base all'età, alla storia e alle esigenze specifiche del nucleo familiare.
              </p>
            </div>
            <div className="md:col-span-5 flex md:justify-end">
              <img
                src={BRAND.photos.withChild}
                alt="Teresa Vilardo con un bambino"
                className="w-full max-w-md aspect-[4/5] object-cover rounded-[36px] shadow-2xl border-4 border-white"
                data-testid="assistenza-hero-photo"
              />
            </div>
          </div>

          <motion.img
            src={BRAND.logo}
            alt=""
            aria-hidden
            style={{ y: bearY, rotate: bearRotate }}
            className="hidden lg:block absolute right-6 top-6 h-28 w-28 rounded-full opacity-60 animate-floaty drop-shadow-2xl pointer-events-none"
          />
        </div>
      </section>

      {/* PRINCIPI COMUNI */}
      <section className="px-6 md:px-10 py-16 md:py-20 max-w-[1500px] mx-auto" data-testid="assistenza-principi">
        <div className="rounded-3xl bg-[#81C3C9]/15 p-8 md:p-14 border-l-4 border-[#FF2768]">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Principi comuni</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#3D2140] leading-[0.95] mb-8">
            Quello che accompagna<br/><span className="text-[#81C3C9]">ogni percorso.</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {PRINCIPI.map((p, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check size={18} className="text-[#FF2768] mt-0.5 flex-shrink-0" />
                <span className="text-[#3D2140] font-semibold">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERCORSI */}
      <section
        id="percorsi"
        className="px-6 md:px-10 py-24 md:py-32 max-w-[1500px] mx-auto"
        data-testid="assistenza-percorsi"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— I percorsi</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Quattro percorsi.<br/>
              <span className="text-[#FF2768]">Un solo obiettivo:</span><br/>
              stare accanto.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PERCORSI.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-white rounded-3xl p-8 md:p-10 border border-[#3D2140]/8 hover:border-[#FF2768]/30 transition-colors"
                data-testid={`assistenza-card-${p.id}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="h-12 w-12 rounded-2xl bg-[#FF2768]/10 flex items-center justify-center">
                    <Icon size={22} className="text-[#FF2768]" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#81C3C9] font-semibold">
                    Percorso 0-6 mesi
                  </span>
                </div>

                <h3 className="font-display text-3xl md:text-4xl text-[#3D2140] leading-tight mb-2">
                  {p.title}
                </h3>
                <p className="text-[#FF2768] italic mb-4">{p.tagline}</p>
                <p className="text-[#3D2140]/75 leading-relaxed mb-6">{p.desc}</p>

                <div className="mb-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 mb-3 font-semibold">
                    Cosa include
                  </p>
                  <ul className="space-y-2">
                    {p.include.map((c, j) => (
                      <li key={j} className="flex items-start gap-3 text-[#3D2140]/85">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#FF2768] flex-shrink-0" />
                        <span className="text-sm leading-relaxed">{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-5 border-t border-[#3D2140]/8">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#3D2140]/60 mb-2 font-semibold">
                    Metodo
                  </p>
                  <p className="text-sm text-[#3D2140]/70 italic leading-relaxed">
                    {p.method}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pacchetti Notturni */}
        <div className="mt-10 rounded-3xl bg-[#3D2140] text-white p-8 md:p-10 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#FF2768]/20 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-8">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-3 font-semibold">
                — Pacchetti Notturni Programmati
              </p>
              <h3 className="font-display text-3xl md:text-4xl leading-tight mb-3">
                Pacchetti di Assistenza Notturna
              </h3>
              <p className="text-white/80 leading-relaxed max-w-2xl">
                Pacchetti di assistenza notturna programmata — <strong>2, 3 o 4 notti</strong> — riservati ai percorsi strutturati. Da prenotare e confermare anticipatamente per garantire continuità e cura personalizzata.
              </p>
            </div>
            <div className="md:col-span-4 md:justify-self-end">
              <a
                href={waLink("Pacchetti di Assistenza Notturna")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-[#FF2768] px-6 py-3 rounded-full text-sm font-semibold hover:scale-105 transition-transform"
              >
                Info pacchetti <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TRASFERTE */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#3D2140] text-white" data-testid="assistenza-trasferte">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12">
            <div className="md:col-span-8">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Trasferte & fuori domicilio</p>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.9]">
                Vengo dove<br/><span className="text-[#81C3C9]">avete bisogno.</span>
              </h2>
              <p className="mt-6 text-white/80 max-w-2xl leading-relaxed">
                Quando l'attività si svolge fuori dal domicilio abituale della famiglia, viene qualificata come <em>trasferta professionale</em>. Concordiamo insieme, prima di partire, tutte le condizioni organizzative ed economiche.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TRASFERTE.map((t, i) => {
              const Icon = t.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="bg-[#3D2140] rounded-3xl p-6 md:p-7 border border-white/8 hover:border-[#FF2768]/50 transition-colors"
                >
                  <div className="h-11 w-11 rounded-xl bg-[#FF2768]/15 flex items-center justify-center mb-4">
                    <Icon size={18} className="text-[#FF2768]" />
                  </div>
                  <h3 className="font-display text-2xl mb-2">{t.title}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{t.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* I PRIMI PASSI PER INIZIARE */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1400px] mx-auto" data-testid="assistenza-primi-passi">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="md:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— I primi passi</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Come iniziamo<br/><span className="text-[#81C3C9]">insieme.</span>
            </h2>
            <p className="mt-6 text-[#3D2140]/75 max-w-2xl leading-relaxed text-base md:text-lg">
              Per avviare un percorso di assistenza neonatale è previsto un primo contatto conoscitivo, finalizzato a comprendere i bisogni del bambino e della famiglia. Costruiamo insieme fiducia, chiarezza e collaborazione fin dal principio.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.09 }}
              className="bg-[#FAF8F5] rounded-3xl p-6 md:p-7 border border-[#3D2140]/6 hover:border-[#FF2768]/40 transition-colors relative"
            >
              <span className="font-display text-5xl text-[#FF2768] leading-none">{s.n}</span>
              <h3 className="font-display text-xl md:text-2xl text-[#3D2140] mt-4 mb-2 leading-tight">
                {s.title}
              </h3>
              <p className="text-sm text-[#3D2140]/70 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Nota contratto */}
        <div className="mt-10 rounded-2xl bg-white border border-[#3D2140]/8 p-6 md:p-8 flex items-start gap-5">
          <ShieldCheck size={28} className="text-[#FF2768] flex-shrink-0 mt-1" />
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] mb-2 font-semibold">A tutela reciproca</p>
            <p className="text-[#3D2140] leading-relaxed">
              Per garantire serenità e tutela reciproca, l'inizio del percorso è accompagnato dalla <strong>sottoscrizione di un accordo professionale</strong> che definisce modalità operative, organizzative ed economiche. Un gesto di rispetto verso il tempo di entrambe le parti — familiare e professionale.
            </p>
          </div>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="px-6 md:px-10 py-24 md:py-32" data-testid="assistenza-cta">
        <div className="max-w-[1200px] mx-auto">
          <div className="relative overflow-hidden rounded-[32px] md:rounded-[48px] bg-gradient-to-br from-[#FF2768] to-[#E01F5A] p-8 md:p-14 lg:p-20 shadow-2xl">
            <img
              src={BRAND.logo}
              alt=""
              aria-hidden
              className="absolute -right-16 -top-16 md:-right-20 md:-top-20 h-64 w-64 md:h-96 md:w-96 opacity-15 rotate-12 pointer-events-none"
            />
            <div className="absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-[#81C3C9]/30 blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
              <div className="md:col-span-8">
                <p className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-white/90 mb-5 font-semibold">
                  <Heart size={12} className="inline mr-1" /> Un primo passo, insieme
                </p>
                <h3 className="font-display text-4xl md:text-6xl lg:text-7xl text-white leading-[0.9] mb-4">
                  Iniziamo con<br/>una chiacchierata.
                </h3>
                <p className="text-white/90 text-base md:text-lg leading-relaxed max-w-xl">
                  Scrivimi su WhatsApp: raccontami la vostra situazione e vediamo insieme quale percorso si adatta ai vostri bisogni 🍼
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <a
                  href={waLink("Assistenza Neonatale 0-6 mesi")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="assistenza-whatsapp-cta"
                  className="group inline-flex items-center gap-3 bg-white text-[#FF2768] rounded-full px-8 md:px-10 py-4 md:py-5 font-semibold text-base md:text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                >
                  <MessageCircle size={20} />
                  Scrivimi su WhatsApp
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
